import { useEffect, useState } from 'react';
import { api, type ClassificationResult, type Objective } from './api';
import { getHandshakeToken, notifyTicketCreated, onHandshake } from './postmessage';
import { HARDCODED_COMPANY_CONTEXT, HARDCODED_OBJECTIVES } from './objectives';
import {
  C,
  SEVERITY_DEFINITIONS,
  SEVERITY_PROMPT,
  TYPE_EMOJI,
  URGENCY_DRIVERS,
  inputStyle,
  mono,
  sans,
  serif,
  type SeverityKey,
} from './constants';

// ─── HOW IT WORKS ─────────────────────────────────────────────────────────────

function HowItWorks() {
  const steps = [
    { n: '1', title: 'Describe your request', body: 'Start by describing your request in one or two sentences. The system will ask one follow-up question if needed, then provisionally classify it: what type it is, which Primary Objective it serves, and how urgent it is.' },
    { n: '2', title: 'Review the provisional classification and continue', body: "You'll see how the system has classified your request. The classification may be refined by product after you complete the full submission. Continue to fill in the remaining detail." },
    { n: '3', title: 'Complete the form and submit', body: 'Fill in the remaining fields with as much context as possible. The more detail you provide, the more useful the ticket will be for product and engineering. Before it is created, the system will flag anything that might slow down triage or sizing.' },
    { n: '4', title: 'It enters the backlog in Shortcut', body: 'Your submission goes into an importance-ranked unsized backlog. You will receive a direct link to your ticket in Shortcut so you can track its status without needing to chase product.' },
    { n: '5', title: 'Product reviews, engineering sizes, humans decide', body: 'The product team reviews the importance-ranked backlog and pulls candidate items for effort sizing with engineering. Only once both importance and effort are known is a final prioritisation decision made. The system ranks by importance. Sequencing is a human decision.' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
        <p style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.8, margin: 0 }}>
          This is the single place where all work requests at Oxford Online Pharmacy are submitted, classified, and ranked by importance. If a request does not come through here, it will not be actioned.
        </p>
      </div>
      {steps.map(step => (
        <div key={step.n} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', background: C.navy, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, flexShrink: 0, fontFamily: serif, marginTop: 2,
          }}>{step.n}</div>
          <div style={{ background: C.surface, borderRadius: 6, padding: 16, border: `1px solid ${C.border}`, flex: 1 }}>
            <div style={{ fontFamily: serif, fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 6 }}>{step.title}</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.7 }}>{step.body}</div>
          </div>
        </div>
      ))}

      <div style={{ background: C.surface, borderRadius: 6, padding: 20, border: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: serif, fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 16 }}>How importance ranking works</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 4, background: C.goldBg, border: `1px solid ${C.gold}40` }}>
            <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, flexShrink: 0, paddingTop: 2, minWidth: 16 }}>1</span>
            <div>
              <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 3 }}>Primary Objective</div>
              <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.6 }}>
                Every item is mapped to one of the company's Primary Objectives. Primary Objective 1 is considered more important than Primary Objective 2, and so on — this ordering matters when the product team is making difficult trade-offs. In practice, product will pull from across all objectives each sprint to keep everything moving, but the objective order is the first thing to consider when two items are competing for the same slot.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}` }}>
            <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.navyMuted, flexShrink: 0, paddingTop: 2, minWidth: 16 }}>2</span>
            <div>
              <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 3 }}>Severity</div>
              <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.6 }}>
                Within each Primary Objective group, items are ranked by severity: P1 above P2, P2 above P3. Severity is derived from qualifying questions in the submission form, not self-declared.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}` }}>
            <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.navyMuted, flexShrink: 0, paddingTop: 2, minWidth: 16 }}>+</span>
            <div>
              <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 3 }}>Second-Order Objective (tiebreaker)</div>
              <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.6 }}>
                Each Primary Objective can have optional Second-Order Objectives beneath it (e.g. "Increase conversion rate" under "Protect and grow revenue"). When an item is tagged with a Second-Order Objective, the product team uses it as a human tiebreaker when curating sprints — items aligned to the current strategic focus get preference. This is a judgement call, not an algorithmic score.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}` }}>
            <span style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.navyMuted, flexShrink: 0, paddingTop: 2, minWidth: 16 }}>i</span>
            <div>
              <div style={{ fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 3 }}>Urgency Driver (informational only)</div>
              <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.6 }}>
                Urgency Driver is visible on every ticket but sits outside the importance ranking. It tells the product team whether a deadline is movable — a regulatory deadline cannot be shifted; a strategic horizon can. It informs sequencing decisions, not importance.
              </div>
            </div>
          </div>
        </div>

        <div style={{ fontFamily: serif, fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 10 }}>Severity levels</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(Object.entries(SEVERITY_DEFINITIONS) as Array<[SeverityKey, typeof SEVERITY_DEFINITIONS[SeverityKey]]>).map(([key, def]) => {
            const colors: Record<SeverityKey, string> = { P1: C.regulatory, P2: C.goldDark, P3: C.navyMuted };
            const bgs: Record<SeverityKey, string> = { P1: C.regulatoryBg, P2: C.goldBg, P3: C.surfaceAlt };
            return (
              <div key={key} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 14px', borderRadius: 4, background: bgs[key], border: `1px solid ${colors[key]}20` }}>
                <span style={{ fontFamily: mono, fontSize: 11, fontWeight: 700, color: colors[key], flexShrink: 0, paddingTop: 1 }}>{key}</span>
                <span style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.5 }}>{def.description}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── OBJECTIVES PANEL (read-only) ────────────────────────────────────────────

function ObjectivesPanel({ objectives }: { objectives: Objective[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: C.surface, borderRadius: 6, padding: 20, border: `1px solid ${C.border}` }}>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Primary Objectives</div>
          <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.5 }}>
            Set and ordered by the CEO. All importance rankings are made relative to these Primary Objectives.
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {objectives.map((obj, i) => (
            <div key={obj.id}>
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 16px', borderRadius: 4,
                background: C.surfaceAlt, border: `1px solid ${C.borderLight}`,
              }}>
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', background: C.navy, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, flexShrink: 0, fontFamily: serif,
                }}>{i + 1}</span>
                <span style={{ flex: 1, fontFamily: sans, fontSize: 13, color: C.navy, lineHeight: 1.4 }}>{obj.text}</span>
                {obj.secondOrder.length > 0 && (
                  <span style={{ fontSize: 10, fontFamily: mono, color: C.navyMuted, flexShrink: 0 }}>
                    {obj.secondOrder.length} second-order
                  </span>
                )}
              </div>
              {obj.secondOrder.length > 0 && (
                <div style={{ paddingLeft: 34, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {obj.secondOrder.map((so, si) => (
                    <div key={so.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px',
                      borderRadius: 4, background: C.surface, border: `1px solid ${C.borderLight}`,
                    }}>
                      <span style={{ fontFamily: mono, fontSize: 10, color: C.navyMuted, flexShrink: 0 }}>{i + 1}.{si + 1}</span>
                      <span style={{ flex: 1, fontFamily: sans, fontSize: 12, color: C.navy }}>{so.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── PROGRESSIVE SUBMISSION ───────────────────────────────────────────────────

type Stage = 'describe' | 'followup' | 'provisional' | 'form' | 'review' | 'submitting';

interface FormState {
  title?: string;
  orderNumber?: string;
  broken?: string;
  expected?: string;
  steps?: string;
  environment?: string;
  volume?: string;
  links?: string;
  what?: string;
  problem?: string;
  objectiveId?: string;
  secondObjectiveId?: string;
  objectiveHow?: string;
  consequence?: string;
  affected?: string;
  evidence?: string;
  urgencyReason?: string;
  urgencyContext?: string;
}

type YesNo = 'yes' | 'no' | null;

interface SubmissionResult {
  id: number;
  app_url: string;
  name: string;
  reasoning: string;
  title: string;
  type: 'Bug' | 'Feature' | 'Chore';
  severity: SeverityKey;
  urgencyDriver: string;
  orderNumber?: string;
  primary_objective_alignment: string;
  primary_objective_label: string;
  objective_number: number;
  second_objective_alignment: string | null;
  second_objective_label: string | null;
  submittedAt: string;
}

function ProgressiveSubmission({
  objectives,
  companyContext,
  onSubmit,
  handshakeReady,
}: {
  objectives: Objective[];
  companyContext: string;
  onSubmit: (data: SubmissionResult) => void;
  handshakeReady: boolean;
}) {
  const [stage, setStage] = useState<Stage>('describe');
  const [description, setDescription] = useState('');
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [followUpAnswer, setFollowUpAnswer] = useState('');
  const [provisional, setProvisional] = useState<ClassificationResult | null>(null);
  const [form, setForm] = useState<FormState>({});
  const [bugQ, setBugQ] = useState<Record<string, YesNo>>({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null });
  const [featureQ, setFeatureQ] = useState<Record<string, YesNo>>({ q1: null, q2: null, q3: null });
  const [urgencyDrivers, setUrgencyDrivers] = useState<string[]>([]);
  const [reviewQuestions, setReviewQuestions] = useState<string[]>([]);
  const [reviewAnswers, setReviewAnswers] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm(f => ({ ...f, [k]: v }));

  const severityColor: Record<SeverityKey, string> = { P1: C.regulatory, P2: C.goldDark, P3: C.navyMuted };
  const severityBg: Record<SeverityKey, string> = { P1: C.regulatoryBg, P2: C.goldBg, P3: C.surfaceAlt };

  // ── DESCRIBE ──
  const handleDescribe = async () => {
    if (!description.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = await api.classify({
        description: description.trim(),
        objectives,
        companyContext,
        severityPrompt: SEVERITY_PROMPT,
      });
      if (parsed.needs_followup && parsed.followup_question) {
        setFollowUpQuestion(parsed.followup_question);
        setProvisional(parsed);
        setStage('followup');
      } else {
        setProvisional(parsed);
        setStage('provisional');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFollowUp = async () => {
    if (!followUpAnswer.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const parsed = await api.classifyFollowup({
        description,
        followupQuestion: followUpQuestion,
        followupAnswer: followUpAnswer.trim(),
        objectives,
        companyContext,
        severityPrompt: SEVERITY_PROMPT,
      });
      setProvisional(parsed);
      setStage('provisional');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProceed = () => {
    if (!provisional) return;
    if (provisional.urgency_driver) setUrgencyDrivers([provisional.urgency_driver]);
    const matchedObj = objectives[(provisional.objective_number ?? 1) - 1];
    if (matchedObj) set('objectiveId', String(matchedObj.id));

    const joined = (description + (followUpAnswer ? ` ${followUpAnswer}` : '')).trim();
    if (!form.title) set('title', provisional.suggested_title || joined);
    if (!form.what) set('what', joined);

    setStage('form');
  };

  const isBug = provisional?.type === 'Bug';
  const isChore = provisional?.type === 'Chore';

  const deriveBugSeverity = (): SeverityKey => {
    if ((bugQ.q1 === 'yes' || bugQ.q3 === 'yes' || bugQ.q4 === 'yes') && bugQ.q5 === 'no') return 'P1';
    if (bugQ.q1 === 'yes' || bugQ.q2 === 'yes' || bugQ.q3 === 'yes' || bugQ.q4 === 'yes' || bugQ.q6 === 'yes') return 'P2';
    return 'P3';
  };

  const deriveFeatureSeverity = (): SeverityKey => {
    if (featureQ.q1 === 'yes' || featureQ.q2 === 'yes' || featureQ.q3 === 'yes') return 'P1';
    if (form.objectiveId) return 'P2';
    return 'P3';
  };

  const severityReady = isBug
    ? Object.values(bugQ).every(v => v !== null)
    : Object.values(featureQ).every(v => v !== null);

  const canSubmit = severityReady
    && urgencyDrivers.length > 0
    && !!form.title
    && (isBug
      ? !!form.broken && !!form.expected && !!form.steps
      : !!form.what && !!form.problem && !!form.objectiveId && !!form.objectiveHow);

  // ── QA REVIEW ──
  const handleSubmitForReview = async () => {
    if (!canSubmit || !provisional) return;
    setStage('review');
    setLoading(true);

    const selectedObjective = objectives.find(o => String(o.id) === String(form.objectiveId));
    const formSummary = isBug
      ? `Type: Bug. Title: ${form.title}. What's broken: ${form.broken || 'not specified'}. Expected: ${form.expected || 'not specified'}. Steps: ${form.steps || 'not specified'}. Environment: ${form.environment || 'not specified'}. Volume: ${form.volume || 'not specified'}.`
      : `Type: ${provisional.type}. Title: ${form.title}. What: ${form.what || 'not specified'}. Problem: ${form.problem || 'not specified'}. Objective: ${selectedObjective?.text}. How it serves it: ${form.objectiveHow || 'not specified'}. Consequence: ${form.consequence || 'not specified'}. Affected: ${form.affected || 'not specified'}. Evidence: ${form.evidence || 'not specified'}.`;

    try {
      const parsed = await api.qaReview({ formSummary });
      setReviewQuestions(parsed.questions ?? []);
    } catch {
      setReviewQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  // ── FINAL SUBMIT → Worker → Shortcut ──
  const handleFinalSubmit = async () => {
    if (!provisional) return;
    setStage('submitting');

    const severity = isBug ? deriveBugSeverity() : deriveFeatureSeverity();
    const selectedObjective = objectives.find(o => String(o.id) === String(form.objectiveId));
    const objectiveNumber = objectives.findIndex(o => String(o.id) === String(form.objectiveId)) + 1;
    const urgencyDriverString = urgencyDrivers.join(', ');

    const secondOrderList = selectedObjective?.secondOrder ?? [];
    const selectedSecondObj = secondOrderList.find(s => String(s.id) === String(form.secondObjectiveId));
    const secondObjectiveNumber = secondOrderList.findIndex(s => String(s.id) === String(form.secondObjectiveId)) + 1;

    const submittedAt = new Date().toISOString();
    const submission = {
      title: form.title ?? '',
      description,
      type: provisional.type,
      severity,
      urgencyDriver: urgencyDriverString,
      urgencyDrivers,
      primary_objective_alignment: selectedObjective?.text ?? '',
      primary_objective_label: `Primary Objective ${objectiveNumber}`,
      objective_number: objectiveNumber,
      second_objective_alignment: selectedSecondObj?.text ?? null,
      second_objective_label: selectedSecondObj ? `Second-Order Objective ${objectiveNumber}.${secondObjectiveNumber}` : null,
      orderNumber: form.orderNumber,
      broken: form.broken,
      expected: form.expected,
      steps: form.steps,
      environment: form.environment,
      volume: form.volume,
      links: form.links,
      what: form.what,
      problem: form.problem,
      objectiveHow: form.objectiveHow,
      consequence: form.consequence,
      affected: form.affected,
      evidence: form.evidence,
      urgencyReason: form.urgencyReason,
      urgencyContext: form.urgencyContext,
      submittedAt,
    };

    try {
      const result = await api.submit({ submission, objectives });
      notifyTicketCreated({ id: result.id, app_url: result.app_url });
      onSubmit({
        id: result.id,
        app_url: result.app_url,
        name: result.name,
        reasoning: result.reasoning,
        title: submission.title,
        type: submission.type,
        severity: submission.severity,
        urgencyDriver: submission.urgencyDriver,
        orderNumber: submission.orderNumber,
        primary_objective_alignment: submission.primary_objective_alignment,
        primary_objective_label: submission.primary_objective_label,
        objective_number: submission.objective_number,
        second_objective_alignment: submission.second_objective_alignment,
        second_objective_label: submission.second_objective_label,
        submittedAt: new Date(submittedAt).toLocaleString(),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Submission failed. Please try again.');
      setStage('review');
    }
  };

  // ── Handshake gate ──
  if (!handshakeReady) {
    return (
      <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
        <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Connecting…</div>
        <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.6 }}>
          Waiting for a secure session handshake from the admin app. If this doesn't clear within a few seconds, refresh the page.
        </div>
      </div>
    );
  }

  // ── STAGE: DESCRIBE ──
  if (stage === 'describe') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 6 }}>What do you need?</div>
          <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, marginBottom: 20, lineHeight: 1.6 }}>
            Describe your request in one or two sentences. The system will classify it and ask one follow-up question if needed before you complete the full submission.
          </div>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder='e.g. "Customers on iOS cannot complete checkout after entering card details" or "We need to add Klarna as a payment option for weight loss products"'
            style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }}
          />
          {error && <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 4, background: C.regulatoryBg, border: `1px solid ${C.regulatory}30`, fontSize: 12, color: C.regulatory, fontFamily: sans }}>{error}</div>}
          <button
            onClick={handleDescribe}
            disabled={loading || !description.trim()}
            style={{
              marginTop: 12, padding: '11px 28px', borderRadius: 4, border: 'none',
              background: loading || !description.trim() ? C.borderLight : C.navy,
              color: loading || !description.trim() ? C.navyMuted : '#fff',
              fontFamily: sans, fontSize: 13, fontWeight: 700,
              cursor: loading || !description.trim() ? 'default' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >{loading ? 'Classifying...' : 'Continue'}</button>
        </div>
      </div>
    );
  }

  // ── STAGE: FOLLOW-UP ──
  if (stage === 'followup') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>One quick question</div>
          <div style={{ padding: '14px 16px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, marginBottom: 16 }}>
            <div style={{ fontFamily: sans, fontSize: 11, color: C.navyMuted, marginBottom: 4 }}>You described:</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navy, fontStyle: 'italic' }}>"{description}"</div>
          </div>
          <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 12 }}>{followUpQuestion}</div>
          <textarea
            value={followUpAnswer}
            onChange={e => setFollowUpAnswer(e.target.value)}
            placeholder="Your answer..."
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          />
          {error && <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 4, background: C.regulatoryBg, border: `1px solid ${C.regulatory}30`, fontSize: 12, color: C.regulatory, fontFamily: sans }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button
              onClick={handleFollowUp}
              disabled={loading || !followUpAnswer.trim()}
              style={{
                padding: '11px 28px', borderRadius: 4, border: 'none',
                background: loading || !followUpAnswer.trim() ? C.borderLight : C.navy,
                color: loading || !followUpAnswer.trim() ? C.navyMuted : '#fff',
                fontFamily: sans, fontSize: 13, fontWeight: 700,
                cursor: loading || !followUpAnswer.trim() ? 'default' : 'pointer',
              }}
            >{loading ? 'Classifying...' : 'Continue'}</button>
            <button onClick={() => setStage('provisional')} style={{
              padding: '11px 20px', borderRadius: 4, border: `1px solid ${C.border}`,
              background: 'transparent', color: C.navyMuted,
              fontFamily: sans, fontSize: 12, cursor: 'pointer',
            }}>Skip</button>
          </div>
        </div>
      </div>
    );
  }

  // ── STAGE: PROVISIONAL ──
  if (stage === 'provisional' && provisional) {
    const sColor = severityColor[provisional.severity];
    const sBg = severityBg[provisional.severity];
    const typeColors: Record<'Bug' | 'Feature' | 'Chore', string> = { Bug: C.regulatory, Feature: C.teal, Chore: C.bau };
    const typeBgs: Record<'Bug' | 'Feature' | 'Chore', string> = { Bug: C.regulatoryBg, Feature: C.tealBg, Chore: C.bauBg };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sColor}` }}>
          <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Provisional classification</div>

          <div style={{ padding: '12px 16px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, marginBottom: 16, fontFamily: sans, fontSize: 13, color: C.navy, fontStyle: 'italic' }}>
            "{provisional.clean_description || (description + (followUpAnswer ? ` — ${followUpAnswer}` : ''))}"
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, fontWeight: 700, background: typeBgs[provisional.type], color: typeColors[provisional.type], fontFamily: mono }}>{TYPE_EMOJI[provisional.type]} {provisional.type}</span>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, fontWeight: 700, background: sBg, color: sColor, fontFamily: mono }}>{provisional.severity}</span>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navyMuted, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>{provisional.urgency_driver}</span>
          </div>

          <div style={{ padding: '12px 16px', borderRadius: 4, background: C.goldBg, border: `1px solid ${C.gold}50`, marginBottom: 14 }}>
            <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Primary Objective Alignment</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navy, fontWeight: 600 }}>Objective {provisional.objective_number}: {provisional.primary_objective_alignment}</div>
          </div>

          <div style={{ borderLeft: `2px solid ${C.gold}`, paddingLeft: 14, marginBottom: 20 }}>
            <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Reasoning</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.7 }}>{provisional.reasoning}</div>
          </div>

          <div style={{ padding: '12px 16px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, marginBottom: 20, fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.6 }}>
            Based on your description, here is how this item has been provisionally classified. The classification may be refined by product after you complete the full submission.
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={handleProceed} style={{
              padding: '12px 28px', borderRadius: 4, border: 'none',
              background: C.navy, color: '#fff',
              fontFamily: sans, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}>Continue to full submission</button>
            <button onClick={() => { setStage('describe'); setDescription(''); setProvisional(null); setFollowUpAnswer(''); }} style={{
              padding: '12px 20px', borderRadius: 4, border: `1px solid ${C.border}`,
              background: 'transparent', color: C.navyMuted,
              fontFamily: sans, fontSize: 12, cursor: 'pointer',
            }}>Start again</button>
          </div>
        </div>
      </div>
    );
  }

  // ── STAGE: FORM ──
  if (stage === 'form' && provisional) {
    const sColor = severityColor[provisional.severity];
    const sBg = severityBg[provisional.severity];
    const bugQuestions = [
      { key: 'q1', label: 'Does this prevent customers from completing orders?' },
      { key: 'q2', label: 'Does this prevent clinicians from reviewing consultations?' },
      { key: 'q3', label: 'Does this pose a data, safety, or regulatory risk?' },
      { key: 'q4', label: 'Is a major acquisition channel completely down or unable to spend?' },
      { key: 'q6', label: 'Does this materially degrade a key user journey or revenue-generating flow?' },
      { key: 'q5', label: 'Does a workaround exist?' },
    ];
    const featureQuestions = [
      { key: 'q1', label: 'Does this directly block the business from operating or generating revenue?' },
      { key: 'q2', label: 'Does this directly block a P1 item from proceeding?' },
      { key: 'q3', label: 'Is there a regulatory or contractual deadline that cannot be moved?' },
    ];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 16, border: `1px solid ${C.border}`, borderLeft: `4px solid ${sColor}` }}>
          <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>Classified as</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, fontWeight: 700, background: sBg, color: sColor, fontFamily: mono }}>{provisional.severity}</span>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.goldBg, color: C.goldDark, fontFamily: mono, border: `1px solid ${C.gold}50` }}>Objective {provisional.objective_number}</span>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navyMuted, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>{TYPE_EMOJI[provisional.type]} {provisional.type}</span>
            <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navyMuted, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>{provisional.urgency_driver}</span>
          </div>
        </div>

        <div style={{ background: C.surface, borderRadius: 6, padding: 20, border: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Complete your submission</div>
          <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, marginBottom: 20, lineHeight: 1.6 }}>
            The more detail you provide, the more useful the ticket will be. Required fields are marked with *.
          </div>

          <FormField label="Title" required hint="A short, scannable name for this item">
            <textarea value={form.title ?? ''} onChange={e => set('title', e.target.value)} placeholder="e.g. Checkout fails on iOS Safari after card entry" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
          </FormField>

          {(isBug || isChore) && (
            <FormField label="Reference / order number" optional>
              <textarea value={form.orderNumber ?? ''} onChange={e => set('orderNumber', e.target.value)} placeholder="e.g. SC-1042 or an internal reference" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
            </FormField>
          )}

          {isBug && (
            <>
              <FormField label="What is broken" required>
                <textarea value={form.broken ?? ''} onChange={e => set('broken', e.target.value)} placeholder="Describe the observed behaviour" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="What should happen instead" required>
                <textarea value={form.expected ?? ''} onChange={e => set('expected', e.target.value)} placeholder="Describe the expected behaviour" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Steps to reproduce" required>
                <textarea value={form.steps ?? ''} onChange={e => set('steps', e.target.value)} placeholder="1. Go to... 2. Click... 3. See..." style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Environment" hint="Device, browser, production or staging">
                <textarea value={form.environment ?? ''} onChange={e => set('environment', e.target.value)} placeholder="e.g. iPhone 14, Safari, production" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
              </FormField>
              <FormField label="Affected volume" hint="User volume or transaction value if known">
                <textarea value={form.volume ?? ''} onChange={e => set('volume', e.target.value)} placeholder="e.g. ~200 orders/day affected, ~£8k daily revenue at risk" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
              </FormField>
              <FormField label="Links / screenshots">
                <textarea value={form.links ?? ''} onChange={e => set('links', e.target.value)} placeholder="Paste any relevant links, screen recordings, or describe attachments" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Which Primary Objective does this affect" optional hint="Helps product triage this bug against the backlog.">
                <ObjectiveSelect objectives={objectives} form={form} set={set} />
              </FormField>
            </>
          )}

          {!isBug && (
            <>
              <FormField label="What is it" required hint="One sentence">
                <textarea value={form.what ?? ''} onChange={e => set('what', e.target.value)} placeholder="e.g. Migrate payment provider from Stripe to Adyen" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
              </FormField>
              <FormField label="What problem does it solve" required>
                <textarea value={form.problem ?? ''} onChange={e => set('problem', e.target.value)} placeholder="Describe the underlying problem or opportunity" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Which Primary Objective does it serve" required>
                <ObjectiveSelect objectives={objectives} form={form} set={set} showSecondOrder />
                <label style={{ display: 'block', fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginTop: 8, marginBottom: 4 }}>How does it serve that Primary Objective <span style={{ color: C.regulatory }}>*</span></label>
                <textarea value={form.objectiveHow ?? ''} onChange={e => set('objectiveHow', e.target.value)} placeholder="e.g. Unblocks Apple Pay and improves checkout conversion by removing payment friction" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="What happens if we don't do it">
                <textarea value={form.consequence ?? ''} onChange={e => set('consequence', e.target.value)} placeholder="Describe the consequence of not doing this" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Who is affected and at what scale">
                <textarea value={form.affected ?? ''} onChange={e => set('affected', e.target.value)} placeholder="e.g. All customers at checkout, ~600 orders/day" style={{ ...inputStyle, minHeight: 48, resize: 'vertical' }} />
              </FormField>
              <FormField label="Supporting evidence or data">
                <textarea value={form.evidence ?? ''} onChange={e => set('evidence', e.target.value)} placeholder="Any data, research, or prior experiments that support this" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Claimed urgency and the reason for it">
                <textarea value={form.urgencyReason ?? ''} onChange={e => set('urgencyReason', e.target.value)} placeholder="e.g. JEM contract commits us to workshops starting March" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
              <FormField label="Links / screenshots">
                <textarea value={form.links ?? ''} onChange={e => set('links', e.target.value)} placeholder="Paste any relevant links, screen recordings, or describe attachments" style={{ ...inputStyle, minHeight: 72, resize: 'vertical' }} />
              </FormField>
            </>
          )}

          <div style={{ background: C.surfaceAlt, borderRadius: 6, padding: 16, border: `1px solid ${C.borderLight}`, marginBottom: 16 }}>
            <div style={{ fontFamily: serif, fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Severity qualifying questions</div>
            <div style={{ fontFamily: sans, fontSize: 11, color: C.navyMuted, marginBottom: 14, lineHeight: 1.5 }}>
              The system assigns severity based on your answers. Engineering may challenge a P1 assignment if they believe the qualifying criteria are not met.
            </div>
            {(isBug ? bugQuestions : featureQuestions).map(q => (
              <div key={q.key} style={{ marginBottom: 12 }}>
                <div style={{ fontFamily: sans, fontSize: 12, color: C.navy, marginBottom: 6 }}>{q.label}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {(['yes', 'no'] as const).map(opt => {
                    const current = isBug ? bugQ[q.key] : featureQ[q.key];
                    const setter = isBug
                      ? () => setBugQ(prev => ({ ...prev, [q.key]: opt }))
                      : () => setFeatureQ(prev => ({ ...prev, [q.key]: opt }));
                    return (
                      <button key={opt} onClick={setter} style={{
                        padding: '5px 16px', borderRadius: 4,
                        border: `1px solid ${current === opt ? C.navy : C.border}`,
                        background: current === opt ? C.navy : C.surfaceAlt,
                        color: current === opt ? '#fff' : C.navyMuted,
                        fontFamily: sans, fontSize: 12, cursor: 'pointer', textTransform: 'capitalize',
                        fontWeight: current === opt ? 700 : 400,
                      }}>{opt}</button>
                    );
                  })}
                </div>
              </div>
            ))}
            {severityReady && (
              <div style={{ marginTop: 14, padding: '8px 14px', borderRadius: 4, background: C.surface, border: `1px solid ${C.border}` }}>
                <span style={{ fontFamily: mono, fontSize: 10, color: C.navyMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Derived severity: </span>
                <span style={{ fontFamily: mono, fontSize: 12, fontWeight: 700, color: severityColor[isBug ? deriveBugSeverity() : deriveFeatureSeverity()] }}>
                  {isBug ? deriveBugSeverity() : deriveFeatureSeverity()}
                </span>
              </div>
            )}
          </div>

          <FormField label="Urgency Driver" required hint="Why does this need to happen when it does? Select all that apply.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              {URGENCY_DRIVERS.map(d => {
                const selected = urgencyDrivers.includes(d);
                return (
                  <button key={d} onClick={() => setUrgencyDrivers(prev => selected ? prev.filter(x => x !== d) : [...prev, d])} style={{
                    padding: '6px 14px', borderRadius: 4,
                    border: `1px solid ${selected ? C.navy : C.border}`,
                    background: selected ? C.navy : C.surfaceAlt,
                    color: selected ? '#fff' : C.navyMuted,
                    fontFamily: sans, fontSize: 11, cursor: 'pointer',
                    fontWeight: selected ? 700 : 400,
                  }}>{d}</button>
                );
              })}
            </div>
            <textarea value={form.urgencyContext ?? ''} onChange={e => set('urgencyContext', e.target.value)} placeholder="Any additional context on urgency (optional)" style={{ ...inputStyle, minHeight: 60, resize: 'vertical' }} />
          </FormField>

          <button
            onClick={handleSubmitForReview}
            disabled={!canSubmit}
            style={{
              padding: '12px 28px', borderRadius: 4, border: 'none',
              background: !canSubmit ? C.borderLight : C.navy,
              color: !canSubmit ? C.navyMuted : '#fff',
              fontFamily: sans, fontSize: 14, fontWeight: 700,
              cursor: !canSubmit ? 'default' : 'pointer',
              transition: 'all 0.15s ease',
            }}
          >Submit request</button>
          {!canSubmit && (
            <div style={{ marginTop: 8, fontFamily: sans, fontSize: 11, color: C.navyMuted }}>Complete all required fields and qualifying questions to submit.</div>
          )}
        </div>
      </div>
    );
  }

  // ── STAGE: REVIEW ──
  if (stage === 'review') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
          {loading ? (
            <>
              <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Reviewing your submission...</div>
              <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.6 }}>Checking for anything that might slow down triage or sizing.</div>
            </>
          ) : reviewQuestions.length === 0 ? (
            <>
              <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Your submission looks good</div>
              <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, marginBottom: 20, lineHeight: 1.6 }}>No gaps found. Ready to submit.</div>
              {error && <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 4, background: C.regulatoryBg, border: `1px solid ${C.regulatory}30`, fontSize: 12, color: C.regulatory, fontFamily: sans }}>{error}</div>}
              <button onClick={handleFinalSubmit} style={{
                padding: '12px 28px', borderRadius: 4, border: 'none',
                background: C.navy, color: '#fff',
                fontFamily: sans, fontSize: 14, fontWeight: 700, cursor: 'pointer',
              }}>Confirm and submit</button>
            </>
          ) : (
            <>
              <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 4 }}>A few things worth clarifying</div>
              <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, marginBottom: 20, lineHeight: 1.6 }}>
                These gaps could slow down triage or sizing. Answer what you can, or skip and submit as is.
              </div>
              {reviewQuestions.map((q, i) => (
                <div key={i} style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 6 }}>{q}</label>
                  <textarea
                    value={reviewAnswers[i] ?? ''}
                    onChange={e => setReviewAnswers(prev => ({ ...prev, [i]: e.target.value }))}
                    placeholder="Your answer (optional)"
                    style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }}
                  />
                </div>
              ))}
              {error && <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 4, background: C.regulatoryBg, border: `1px solid ${C.regulatory}30`, fontSize: 12, color: C.regulatory, fontFamily: sans }}>{error}</div>}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                <button onClick={handleFinalSubmit} style={{
                  padding: '12px 28px', borderRadius: 4, border: 'none',
                  background: C.navy, color: '#fff',
                  fontFamily: sans, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>Submit</button>
                <button onClick={handleFinalSubmit} style={{
                  padding: '12px 20px', borderRadius: 4, border: `1px solid ${C.border}`,
                  background: 'transparent', color: C.navyMuted,
                  fontFamily: sans, fontSize: 12, cursor: 'pointer',
                }}>Skip and submit as is</button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (stage === 'submitting') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}` }}>
          <div style={{ fontFamily: serif, fontSize: 16, fontWeight: 700, color: C.navy, marginBottom: 8 }}>Submitting...</div>
          <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.6 }}>Creating your ticket in Shortcut.</div>
        </div>
      </div>
    );
  }

  return null;
}

// ─── Form field wrapper ───────────────────────────────────────────────────────

function FormField({
  label, required, optional, hint, children,
}: { label: string; required?: boolean; optional?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: C.regulatory }}> *</span>}
        {optional && <span style={{ fontWeight: 400, color: C.navyMuted }}> (optional)</span>}
      </label>
      {hint && <div style={{ fontFamily: sans, fontSize: 11, color: C.navyMuted, marginBottom: 6 }}>{hint}</div>}
      {children}
    </div>
  );
}

// ─── Objective select (+ optional second-order) ───────────────────────────────

function ObjectiveSelect({
  objectives, form, set, showSecondOrder,
}: {
  objectives: Objective[];
  form: FormState;
  set: <K extends keyof FormState>(k: K, v: FormState[K]) => void;
  showSecondOrder?: boolean;
}) {
  const selected = objectives.find(o => String(o.id) === String(form.objectiveId));
  const primaryIndex = objectives.findIndex(o => String(o.id) === String(form.objectiveId));
  const secondOrder = selected?.secondOrder ?? [];

  return (
    <>
      <select
        value={form.objectiveId ?? ''}
        onChange={e => { set('objectiveId', e.target.value); set('secondObjectiveId', ''); }}
        style={{ ...inputStyle, marginBottom: 8, cursor: 'pointer', appearance: 'auto' }}
      >
        <option value="">Select an objective...</option>
        {objectives.map((o, i) => <option key={o.id} value={o.id}>{i + 1}. {o.text}</option>)}
      </select>
      {showSecondOrder && form.objectiveId && secondOrder.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', fontFamily: sans, fontSize: 12, fontWeight: 700, color: C.navy, marginBottom: 4 }}>
            Second-Order Objective <span style={{ fontWeight: 400, color: C.navyMuted }}>(optional)</span>
          </label>
          <div style={{ fontFamily: sans, fontSize: 11, color: C.navyMuted, marginBottom: 6 }}>
            If your item aligns to a specific focus area within this objective, select it here. Product uses this as a tiebreaker when curating sprints.
          </div>
          <select value={form.secondObjectiveId ?? ''} onChange={e => set('secondObjectiveId', e.target.value)} style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}>
            <option value="">None</option>
            {secondOrder.map((so, si) => <option key={so.id} value={so.id}>{primaryIndex + 1}.{si + 1} {so.text}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

// ─── CLASSIFICATION SUMMARY ───────────────────────────────────────────────────

function ClassificationSummary({ submission, onBack }: { submission: SubmissionResult; onBack: () => void }) {
  const severityColor: Record<SeverityKey, string> = { P1: C.regulatory, P2: C.goldDark, P3: C.navyMuted };
  const severityBg: Record<SeverityKey, string> = { P1: C.regulatoryBg, P2: C.goldBg, P3: C.surfaceAlt };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ background: C.surface, borderRadius: 6, padding: 24, border: `1px solid ${C.border}`, borderLeft: `4px solid ${severityColor[submission.severity]}` }}>
        <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
          Submitted {submission.submittedAt}
        </div>
        <div style={{ fontFamily: serif, fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 4, lineHeight: 1.3 }}>
          {submission.title}
        </div>
        {submission.orderNumber && (
          <div style={{ fontFamily: mono, fontSize: 11, color: C.navyMuted, marginBottom: 16 }}>{submission.orderNumber}</div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, fontWeight: 700, background: severityBg[submission.severity], color: severityColor[submission.severity], fontFamily: mono }}>{submission.severity}</span>
          <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navy, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>Objective {submission.objective_number}</span>
          <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navyMuted, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>{submission.urgencyDriver}</span>
          <span style={{ padding: '4px 12px', borderRadius: 3, fontSize: 11, background: C.surfaceAlt, color: C.navyMuted, fontFamily: mono, border: `1px solid ${C.borderLight}` }}>{TYPE_EMOJI[submission.type]} {submission.type}</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', borderRadius: 4, background: C.goldBg, border: `1px solid ${C.gold}50` }}>
            <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{submission.primary_objective_label}</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navy, fontWeight: 600 }}>{submission.primary_objective_alignment}</div>
          </div>
          {submission.second_objective_alignment && (
            <div style={{ padding: '12px 16px', borderRadius: 4, background: C.tealBg, border: `1px solid ${C.teal}30` }}>
              <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.teal, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{submission.second_objective_label}</div>
              <div style={{ fontFamily: sans, fontSize: 13, color: C.navy, fontWeight: 600 }}>{submission.second_objective_alignment}</div>
            </div>
          )}
          <div style={{ padding: '12px 16px', borderRadius: 4, background: severityBg[submission.severity], border: `1px solid ${severityColor[submission.severity]}20` }}>
            <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: severityColor[submission.severity], textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Severity</div>
            <div style={{ fontFamily: sans, fontSize: 13, color: C.navy }}>{SEVERITY_DEFINITIONS[submission.severity].description}</div>
          </div>
        </div>

        <div style={{ borderLeft: `2px solid ${C.gold}`, paddingLeft: 14, marginBottom: 20 }}>
          <div style={{ fontFamily: mono, fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Why it was classified this way</div>
          <div style={{ fontFamily: sans, fontSize: 13, color: C.navyMuted, lineHeight: 1.7 }}>{submission.reasoning}</div>
        </div>

        <div style={{ padding: '14px 16px', borderRadius: 4, background: C.surfaceAlt, border: `1px solid ${C.borderLight}`, marginBottom: 16 }}>
          <div style={{ fontFamily: serif, fontSize: 13, fontWeight: 700, color: C.navy, marginBottom: 6 }}>What happens next</div>
          <div style={{ fontFamily: sans, fontSize: 12, color: C.navyMuted, lineHeight: 1.7 }}>
            Your item has entered the importance-ranked backlog in Shortcut. The product team reviews the backlog regularly and pulls candidate items for effort sizing with engineering. Only once both importance and effort are known is a final prioritisation decision made. You do not need to chase for an update.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <a href={submission.app_url} target="_blank" rel="noopener noreferrer" style={{
            padding: '10px 20px', borderRadius: 4, border: 'none', background: C.navy, color: '#fff',
            fontFamily: sans, fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', display: 'inline-block',
          }}>View ticket in Shortcut</a>
          <button onClick={onBack} style={{
            padding: '10px 20px', borderRadius: 4, border: `1px solid ${C.border}`,
            background: C.surfaceAlt, color: C.navyMuted, fontFamily: sans, fontSize: 12, cursor: 'pointer',
          }}>Submit another</button>
        </div>
      </div>
    </div>
  );
}

// ─── ROOT ─────────────────────────────────────────────────────────────────────

export default function App() {
  const [tab, setTab] = useState<'how' | 'objectives' | 'submit' | 'summary'>('how');
  const [submission, setSubmission] = useState<SubmissionResult | null>(null);
  const [handshakeReady, setHandshakeReady] = useState(!!getHandshakeToken());
  const [myTicketsUrl, setMyTicketsUrl] = useState<string | null>(null);

  useEffect(() => {
    const unsub = onHandshake(() => setHandshakeReady(true));
    return unsub;
  }, []);

  // Once the handshake lands, ask the Worker for the submitter's Shortcut
  // mention name + pre-built search URL so we can render a "View my open
  // tickets" link. Best-effort — silent failure if the admin isn't a
  // Shortcut member or /api/me errors. Doesn't gate any other behaviour.
  useEffect(() => {
    if (!handshakeReady) return;
    let cancelled = false;
    api.me()
      .then((res) => {
        if (cancelled) return;
        if (res.found && res.search_url) {
          setMyTicketsUrl(res.search_url);
        }
      })
      .catch(() => {
        // Silent — the access-denied path would have served before the
        // iframe loaded if the user genuinely isn't a member. A transient
        // failure here just means no link this page load.
      });
    return () => {
      cancelled = true;
    };
  }, [handshakeReady]);

  const objectives = HARDCODED_OBJECTIVES;
  const companyContext = HARDCODED_COMPANY_CONTEXT;

  const tabs = [
    { key: 'how' as const, label: 'How it works' },
    { key: 'objectives' as const, label: 'Objectives' },
    { key: 'submit' as const, label: 'Submit a request' },
  ];

  const handleSubmit = (data: SubmissionResult) => {
    setSubmission(data);
    setTab('summary');
  };

  return (
    <div style={{ fontFamily: sans, background: C.bg, color: C.text, minHeight: '100vh', padding: '28px 16px' }}>
      <link href="https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap" rel="stylesheet" />

      <div style={{ maxWidth: 820, margin: '0 auto 28px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8, fontFamily: mono }}>Oxford Online Pharmacy</div>
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0, lineHeight: 1.2, color: C.navy, fontFamily: serif }}>Triage</h1>
            <p style={{ fontSize: 13, color: C.navyMuted, margin: '8px 0 0', lineHeight: 1.6, fontFamily: sans }}>
              The single entry point for all work requests at OOP. If it does not come through here, it will not be actioned.
            </p>
          </div>
          {myTicketsUrl !== null && (
            <a
              href={myTicketsUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flexShrink: 0,
                fontSize: 11,
                fontWeight: 600,
                fontFamily: mono,
                color: C.navy,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 4,
                padding: '6px 10px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                marginTop: 4,
              }}
            >
              View my open tickets ↗
            </a>
          )}
        </div>
      </div>

      <div style={{
        maxWidth: 820, margin: '0 auto 20px', display: 'flex', gap: 0,
        background: C.surface, borderRadius: 4, border: `1px solid ${C.border}`, overflow: 'hidden',
      }}>
        {tabs.map((t, i) => (
          <button key={t.key} onClick={() => { setTab(t.key); setSubmission(null); }} style={{
            flex: 1, padding: '10px 8px', border: 'none', cursor: 'pointer',
            fontSize: 11, fontWeight: 700, fontFamily: sans,
            background: tab === t.key ? C.navy : 'transparent',
            color: tab === t.key ? '#fff' : C.navyMuted,
            borderRight: i < tabs.length - 1 ? `1px solid ${C.borderLight}` : 'none',
            transition: 'all 0.15s ease',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        {tab === 'how' && <HowItWorks />}
        {tab === 'objectives' && <ObjectivesPanel objectives={objectives} />}
        {tab === 'submit' && !submission && (
          <ProgressiveSubmission
            objectives={objectives}
            companyContext={companyContext}
            onSubmit={handleSubmit}
            handshakeReady={handshakeReady}
          />
        )}
        {tab === 'summary' && submission && <ClassificationSummary submission={submission} onBack={() => { setSubmission(null); setTab('submit'); }} />}
      </div>

      <div style={{ maxWidth: 820, margin: '32px auto 0', textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: C.textLight, fontFamily: mono, letterSpacing: 0.5 }}>Family Founded · Oxford · Since 1925</div>
      </div>
    </div>
  );
}
